// Icons
import { faCompress, faExpand, faFolder, faHouse, faInfoCircle, faMoon, faSearch, faSignOutAlt, faSun, faTasks, faThLarge, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Images
import wabysLogo from '../../assets/wabys.png';
import watomsLogo from '../../assets/watoms.png';
// Custom Context
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
// Custom Hooks
import useFullScreen from "hooks/useFullScreen";
// React Tools
import { useNavigate } from 'react-router-dom';
// Custom Utils
import fullScreen from "utils/fullScreen";
import { userFullName } from "utils/userFullName";
import { useTheme } from "context/ThemeContext";
import { useState } from "react";

const WatomsNavbar = ({ children, showSearch=true, search, setSearch, backButton }) => {
    const navigate = useNavigate();
    const { logout, userInfo } = useAuth();
    const { language, setLanguage } = useLanguage();
    const isFullScreen = useFullScreen();
    const { darkMode, toggleDarkMode } = useTheme();
    const [menuOpen, setMenuOpen] = useState(false);
    return (
        <div className="relative z-10">
            {/* Logo and Search */}
            <div className="flex flex-col md:flex-row items-center justify-between w-full mx-auto px-6 py-8 gap-8">
                <div className="flex items-center gap-6">
                    <img className={`w-[100px] md:w-[120px] lg:w-[140px] cursor-pointer ${darkMode && "bg-white rounded-full px-2"}`} src={wabysLogo} alt="Wabys Logo" onClick={() => navigate('/wabys')} />
                    <div className='border-l-2 border-gray-500 p-1 h-8' />
                    <img className="w-[100px] md:w-[120px] lg:w-[140px] cursor-pointer" src={watomsLogo} alt="Wabys Logo" onClick={() => navigate('/watoms')} />
                </div>
                {showSearch && <div className="flex-1 flex justify-center">
                    <div className="relative w-full max-w-md">
                        <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                        <input
                            type="text"
                            className="w-full pl-12 pr-4 py-1 rounded-full border border-gray-200 shadow focus:ring-2 focus:ring-watomsBlue bg-white/90 text-lg font-medium placeholder-gray-400 transition-all focus:border-watomsBlue focus:shadow-lg outline-none"
                            placeholder={language ? "Search..." : "ابحث..."}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ fontFamily: 'inherit' }}
                        />
                    </div>
                </div>}
                <div className="flex items-center gap-4 relative flex-wrap justify-evenly">
                    {children}
                    <button onClick={toggleDarkMode} className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all">
                        <FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="text-xl text-watomsBlue" />
                    </button>
                    {/* Full Screen Toggle Button */}
                    <button
                        onClick={fullScreen}
                        className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                        title={language ? (isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen') : (isFullScreen ? 'خروج من الشاشة الكاملة' : 'دخول الشاشة الكاملة')}
                    >
                        <FontAwesomeIcon
                            icon={isFullScreen ? faCompress : faExpand}
                            className="text-xl text-watomsBlue"
                        />
                    </button>
                    {/* Language Toggle Button */}
                    <button
                        className="w-10 h-10 flex justify-center items-center text-watomsBlue rounded-full bg-white/80 hover:bg-gray-200 shadow transition-all font-bold text-base"
                        onClick={() => setLanguage(!language)}
                        title={language ? 'العربية' : 'English'}
                    >
                        {language ? 'AR' : 'EN'}
                    </button>
                    {/* User Info */}
                    <span className="flex items-center gap-2 font-bold text-lg md:min-w-[120px] min-w-[300px] justify-center">
                        <FontAwesomeIcon icon={faUser} className={darkMode ? "text-white" : "text-watomsBlue"} />
                        {userFullName(userInfo, language)}
                    </span>
                    {/* App Switcher Dropdown */}
                    <div className="relative">
                        <button
                            className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                            onClick={() => setMenuOpen(v => !v)}
                            aria-label="App Switcher"
                        >
                            <FontAwesomeIcon icon={faThLarge} className="svg-inline--fa fa-table-cells-large text-xl text-watomsBlue" />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 animate-fadeIn">
                                <button onClick={() => { setMenuOpen(false); navigate('/watoms/pms'); }} className="w-full text-left px-5 py-3 hover:bg-blue-50 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faThLarge} className="text-watomsBlue" /> PMS
                                </button>
                                <button onClick={() => { setMenuOpen(false); navigate('/watoms/dms'); }} className="w-full text-left px-5 py-3 hover:bg-blue-50 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faFolder} className="text-wisdomOrange" /> DMS
                                </button>
                                <button onClick={() => { setMenuOpen(false); navigate('/watoms/tms'); }} className="w-full text-left px-5 py-3 hover:bg-blue-50 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faTasks} className="text-blue-500" /> TMS
                                </button>
                                <button onClick={() => { setMenuOpen(false); navigate('/watoms/dashboard'); }} className="w-full text-left px-5 py-3 hover:bg-blue-50 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-purple-500" /> Dashboard
                                </button>
                            </div>
                        )}
                    </div>
                    {/* Back Button */}
                    {backButton && <button
                        className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                        onClick={() => navigate(backButton)}
                    >
                        <FontAwesomeIcon icon={faHouse} className="text-xl text-green-700" />
                    </button>}
                    {/* Sign Out */}
                    <button
                        className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                        onClick={() => { logout(); navigate('/login'); }}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="text-xl text-wisdomOrange" />
                    </button>
                </div>
            </div>
        </div>
    )
}

export default WatomsNavbar;